import React, { useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import {
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useDebouncedCallback } from "use-debounce";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";

import {
  Button,
  RichTextEditor,
  Input,
  Loader,
  Extensions,
} from "@monolith-forensics/monolith-ui";
import { ArrowLeftIcon, RotateCcwIcon } from "lucide-react";

import { usePermissions } from "@/hooks/usePermissions";
import { useFeatures } from "@/contexts/FeaturesContext";
import MonolithNotesAPI from "@/api/Notes";
import CasesApi from "@/api/cases";
import LoggingAPI from "@/api/logging";

import {
  useVersionsWithNumbers,
  RestoreNoteModal,
  VersionFeed,
} from "@/Features/NoteHistory";

const NoteHistoryPage = () => {
  const { case_id, uuid, note_uuid, version_uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser, hasPermission } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();
  const hasTableSupport = hasFeatures([
    FEATURES.NOTES_AND_REPORTS_TABLE_SUPPORT,
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const [isRestoring, setIsRestoring] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");

  const isEvidenceContext = !!uuid && !case_id;

  const resolveVersionPath = (versionUuid: string) => {
    return isEvidenceContext
      ? `/evidence/${uuid}/notes/${note_uuid}/history/${versionUuid}`
      : `/cases/${case_id}/notes/${note_uuid}/history/${versionUuid}`;
  };

  // Sets current version and updates URL with new versionUuid
  const handleVersionChange = useCallback(
    (versionUuid: string) => {
      // Update URL to reflect the selected version based on context
      const basePath = resolveVersionPath(versionUuid);

      navigate(basePath);
    },
    [isEvidenceContext, uuid, note_uuid, case_id, navigate]
  );

  // Debounced search handler
  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearchTerm(value);
  }, 300);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value); // Update input immediately for responsive UI
    debouncedSearch(value); // Trigger search with debounce
  };

  // Handle clearing search
  const handleClearSearch = () => {
    setInputValue("");
    setSearchTerm("");
  };

  // Handle search input key events
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClearSearch();
      (e.target as HTMLInputElement).blur();
    }
  };

  // Query for case information (only in case context)
  const { data: caseInfo } = useQuery({
    queryKey: [
      "cases",
      "list",
      { case_id: case_id ? parseInt(case_id) : null },
    ],
    queryFn: () =>
      CasesApi.getCases({
        case_id: case_id ? parseInt(case_id) : null,
      }),
    enabled: !!case_id && !isEvidenceContext,
    select: (data) => data?.[0] || {},
  });

  // Query for note information
  const { data: noteInfo } = useQuery({
    queryKey: ["note", "details", note_uuid],
    queryFn: () =>
      MonolithNotesAPI.getNotes({
        uuid: note_uuid,
        include_content: true,
        data_only: true,
      }),
    enabled: !!note_uuid,
    select: (data) => data?.[0] || {},
  });

  // Infinite query for note versions with virtualization support
  const {
    data: versionsInfiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["note-versions", "infinite", note_uuid, searchTerm],
    queryFn: ({ pageParam }) => {
      return MonolithNotesAPI.getNoteVersions({
        note_uuid,
        include_content: false,
        page: pageParam,
        search: searchTerm || undefined,
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.currentPage - 1 === 0) return null;
      return firstPage.currentPage - 1;
    },
    initialPageParam: 1,
    enabled: !!note_uuid,
  });

  // Simplified: just flatten the data without unnecessary processing
  const allVersions = useMemo(
    () =>
      versionsInfiniteData?.pages?.flatMap((page) => page?.data || []) || [],
    [versionsInfiniteData]
  );
  const totalVersions = versionsInfiniteData?.pages?.[0]?.total || 0;

  // Use the custom hook for sorted versions with numbers
  const { sortedVersionsWithNumbers, getVersionInfo } = useVersionsWithNumbers(
    allVersions,
    totalVersions,
    !!searchTerm
  );

  // Query for specific version content (only when version is selected)
  const { data: versionContent, isLoading: isLoadingContent } = useQuery({
    queryKey: ["note-version-content", note_uuid, version_uuid],
    queryFn: () =>
      MonolithNotesAPI.getNoteVersionContent({
        note_uuid,
        version_uuid,
      }),
    enabled: !!note_uuid && !!version_uuid,
  });

  // Show restore confirmation modal
  const handleShowRestoreModal = () => {
    setShowRestoreModal(true);
  };

  // Restore function to update note content to selected version
  const handleRestore = async () => {
    setShowRestoreModal(false);
    if (
      noteInfo?.created_by?.user_id !== currentUser.user_id ||
      !noteInfo ||
      !version_uuid
    ) {
      enqueueSnackbar(
        "Unable to restore: insufficient permissions or missing data",
        {
          variant: "error",
        }
      );
      return;
    }

    setIsRestoring(true);

    try {
      // Fetch the content for the specific version we want to restore
      const versionContentResponse =
        await MonolithNotesAPI.getNoteVersionContent({
          note_uuid,
          version_uuid: version_uuid,
        });

      const versionHtml = versionContentResponse?.data?.[0]?.html;

      if (!versionHtml) {
        enqueueSnackbar("Unable to retrieve version content for restore", {
          variant: "error",
        });
        return;
      }

      const response = await MonolithNotesAPI.updateNote({
        uuid: note_uuid,
        note_data: versionHtml,
      });

      if (response.success === false) {
        enqueueSnackbar("Error restoring note content. Please try again.", {
          variant: "error",
        });
        return;
      }

      // Update the exact query cache that the note editor uses
      const noteEditorQuery = [
        "notes:list",
        {
          uuid: note_uuid,
          include_content: true,
          data_only: true,
        },
      ];

      queryClient.setQueryData(noteEditorQuery, (oldData: any[] = []) => {
        if (!oldData?.[0]) return oldData;
        const newData = [...oldData];
        newData[0] = {
          ...newData[0],
          note_data: versionHtml,
          updated_at: new Date().toISOString(),
          updated_by: {
            full_name: currentUser.full_name,
            user_id: currentUser.user_id,
            title: currentUser.title,
            email: currentUser.email,
          },
        };
        return newData;
      });

      // Also update the note details query used by history page
      queryClient.setQueryData(
        ["note", "details", note_uuid],
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            note_data: versionHtml,
            updated_at: new Date().toISOString(),
            updated_by: {
              full_name: currentUser.full_name,
              user_id: currentUser.user_id,
              title: currentUser.title,
              email: currentUser.email,
            },
          };
        }
      );

      // Invalidate note versions to refetch updated data (infinite query)
      queryClient.invalidateQueries({
        queryKey: ["note-versions", "infinite", note_uuid],
      });

      // Reset case notes queries to ensure fresh data
      queryClient.resetQueries({
        queryKey: ["case:notes"],
      });

      // Reset the specific case-notes queries that handle individual note content
      queryClient.resetQueries({
        queryKey: [
          "case-notes",
          "list",
          { uuid: note_uuid, include_content: true },
        ],
        exact: true,
      });

      // Force refetch of any existing note editor queries to ensure fresh content
      queryClient.refetchQueries({
        queryKey: ["notes:list"],
        type: "active",
      });

      // Log the restore activity
      if (caseInfo?.case_id && selectedVersion) {
        LoggingAPI.createLog({
          case_id: case_id,
          user_id: currentUser.user_id,
          message: `Restored note "${
            noteInfo.note_tag
          }" to version from ${new Date(
            selectedVersion.created_on
          ).toLocaleString()}, ID ${note_uuid}`,
          event: "restore",
          object_type: "note",
          object_uuid: note_uuid,
        });
      }

      enqueueSnackbar(
        "Note content restored successfully! Returning to note view...",
        {
          variant: "success",
        }
      );

      // Navigate back to the note view to show the updated content
      setTimeout(() => {
        const path = isEvidenceContext
          ? `/evidence/${uuid}/notes`
          : `/cases/${case_id}/notes/${note_uuid}`;
        navigate(path);
      }, 500); // Small delay to let user see the success message
    } catch (error) {
      console.error("Error restoring note:", error);
      enqueueSnackbar("Error restoring note content. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsRestoring(false);
    }
  };

  // Get the content for the selected version from the separate query
  const selectedVersion = versionContent?.data?.[0];

  const displayContent =
    selectedVersion?.html ||
    noteInfo?.note_data ||
    (allVersions.length === 0 && !isLoading
      ? `<html><p style="font-style: italic;">This note has no history</p></html>`
      : ``);

  const handleBackToNotes = () => {
    if (isEvidenceContext) {
      navigate(`/evidence/${uuid}/notes`);
    } else {
      navigate(`/cases/${case_id}/notes/${note_uuid}`);
    }
  };

  if (!version_uuid && allVersions.length > 0) {
    // Get most recent version by sorting by created_on timestamp
    const mostRecent = [...allVersions].sort(
      (a, b) =>
        new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
    )[0];

    const basePath = resolveVersionPath(mostRecent.uuid);

    return <Navigate to={basePath} replace />;
  }

  if ((!case_id && !uuid) || !note_uuid) {
    return <div>Invalid route parameters</div>;
  }

  return (
    <>
      <Helmet title={`Note History | ${noteInfo?.note_tag || "Loading..."}`} />
      <NoteSystemContainer className="mf-note-system">
        <PageContainer>
          <Header>
            <HeaderLeft>
              <Button
                size="xs"
                variant="text"
                leftSection={<ArrowLeftIcon size={14} />}
                onClick={handleBackToNotes}
              >
                Back to Notes
              </Button>
            </HeaderLeft>
          </Header>

          <ContentContainer>
            <LeftPane>
              <LeftPaneHeader>
                <NoteTitleContainer>
                  <TitleSection>
                    <TitleRow>
                      <NoteTitle>
                        {noteInfo?.note_tag || "Loading..."}
                      </NoteTitle>
                      {selectedVersion && version_uuid && (
                        <VersionNumber>
                          {(() => {
                            const versionInfo = getVersionInfo(version_uuid);
                            if (versionInfo) {
                              return versionInfo.displayText;
                            }

                            const formattedDate = new Date(
                              selectedVersion.created_on
                            ).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            });

                            return searchTerm ? formattedDate : "Loading...";
                          })()}
                        </VersionNumber>
                      )}
                    </TitleRow>
                  </TitleSection>
                  <div style={{ flex: "0 0 auto", display: "flex", gap: 8 }}>
                    <Button
                      size="xs"
                      variant="outlined"
                      leftSection={<RotateCcwIcon size={14} />}
                      onClick={handleShowRestoreModal}
                      disabled={
                        noteInfo?.created_by?.user_id !== currentUser.user_id ||
                        isRestoring ||
                        !selectedVersion ||
                        !version_uuid
                      }
                    >
                      Restore This Version
                    </Button>
                  </div>
                </NoteTitleContainer>
              </LeftPaneHeader>

              <EditorContainer className="editor-container">
                {isLoading || isLoadingContent ? (
                  <LoadingContainer>
                    <Loader />
                    <div>Loading...</div>
                  </LoadingContainer>
                ) : hasTableSupport ? (
                  <RichTextEditor
                    key={version_uuid}
                    defaultValue={displayContent}
                    readOnly={true}
                    extensions={[Extensions.Image]}
                    handleImageUpload={() => {
                      return Promise.resolve("");
                    }}
                  />
                ) : (
                  <RichTextEditor
                    key={version_uuid}
                    defaultValue={displayContent}
                    readOnly={true}
                    extensions={[Extensions.Image]}
                    handleImageUpload={() => {
                      return Promise.resolve("");
                    }}
                  />
                )}
              </EditorContainer>
            </LeftPane>

            <RightPane>
              <>
                <RightPaneHeader>
                  <VersionsTitle>Version History</VersionsTitle>

                  <VersionCount>
                    {totalVersions} version{totalVersions !== 1 ? "s" : ""}
                  </VersionCount>
                </RightPaneHeader>

                <SearchContainer>
                  <Input
                    placeholder="Search note versions..."
                    variant="outlined"
                    height={30}
                    value={inputValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                  />
                  {inputValue && (
                    <SearchInfo>
                      {isLoading
                        ? `Searching for "${inputValue}"...`
                        : `Showing results for "${inputValue}"`}
                    </SearchInfo>
                  )}
                </SearchContainer>
                <VersionFeed
                  versions={sortedVersionsWithNumbers}
                  userName={noteInfo?.created_by?.full_name}
                  onItemSelect={handleVersionChange}
                  selectedVersionUuid={version_uuid}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  searchTerm={searchTerm}
                  isLoading={isLoading}
                />
              </>
            </RightPane>
          </ContentContainer>
        </PageContainer>
        <RestoreNoteModal
          show={showRestoreModal}
          onClose={() => setShowRestoreModal(false)}
          onConfirm={handleRestore}
          onCancel={() => setShowRestoreModal(false)}
          isRestoring={isRestoring}
          selectedVersion={selectedVersion}
          noteTitle={noteInfo?.note_tag || "Note"}
          noteCreatedOn={noteInfo?.created_on}
          userName={noteInfo?.created_by?.full_name}
        />
      </NoteSystemContainer>
    </>
  );
};

// Overall container styled like NoteSystem wrapper
const NoteSystemContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: ${({ theme }) => theme.palette.background.default};
  transition: all 0.3s ease;
`;

// Main container styled like NoteSystem's MainNoteView
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: calc(100dvh - 265px);
  background-color: ${({ theme }) => theme.palette.background.default};
  transition: all 0.3s ease;
`;

// Header styled like NoteSystem tabs/navigation
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  height: 35px;
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

// Add new styled component for the centered note title in left pane
const NoteTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 24px 12px 24px;
  background-color: ${({ theme }) => theme.palette.background.default};
  width: 100%;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const NoteTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const VersionNumber = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-weight: 400;
`;

// Content area styled like NoteSystem's NotesSection
const ContentContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.palette.divider};
`;

// Left pane styled like NoteSystem's NoteEditorContainer
const LeftPane = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.palette.background.default};
  flex-grow: 1;
  min-width: 20rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border-right: 1px solid ${({ theme }) => theme.palette.divider};
`;

// Container for restore button at top of left pane
const LeftPaneHeader = styled.div`
  display: flex;
  justify-content: stretch;
  background-color: ${({ theme }) => theme.palette.background.default};
  width: 100%;
`;

// Container for the editor content
const EditorContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  width: 100%;

  padding-right: 5px;
`;

// Right pane styled like NoteSystem's NotesList
const RightPane = styled.div`
  position: relative;
  padding: 0.75rem;
  height: 100%;
  width: 20rem;
  min-width: 20rem;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const RightPaneHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 12px 0;
`;

const VersionsTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const VersionCount = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-weight: 400;
`;

const LoadingContainer = styled.div`
  position: absolute;
  top: 25%;
  left: 50%;
  transform: translate(-50%, -25%);

  width: 200px;
  height: 100px;

  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;

  padding: 24px 24px;
  font-size: 14px;
  font-weight: 500;
  background-color: ${({ theme }) => theme.palette.background.alt};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  color: ${({ theme }) => theme.palette.text.secondary};

  /* Animation for fade in/out */
  animation: fadeInOut 0.3s ease-in-out;

  @keyframes fadeInOut {
    0% {
      opacity: 0;
      transform: translate(-50%, -25%) scale(0.95);
    }
    100% {
      opacity: 1;
      transform: translate(-50%, -25%) scale(1);
    }
  }
`;

const SearchContainer = styled.div`
  flex-shrink: 0;
  margin-bottom: 10px;
`;

const SearchInfo = styled.div`
  margin-top: 8px;
  padding: 4px 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-style: italic;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default NoteHistoryPage;
